/** Safe linkify — renders plain text with URLs converted to anchor
 *  tags. No HTML injection: we split the text on a URL regex and emit
 *  React fragments only.
 *
 *  Used by CEO chat / project board to make share + board links
 *  clickable inside agent-generated messages.
 */
import { Copy } from "lucide-react";

const URL_RE = /(https?:\/\/[^\s)]+)/g;
// Trim trailing punctuation that's almost never part of the URL.
const STRIP_TRAIL = /[.,!?;:'"`)]+$/;

function trimUrl(raw) {
    let url = raw;
    let trail = "";
    while (STRIP_TRAIL.test(url)) {
        trail = url.slice(-1) + trail;
        url = url.slice(0, -1);
    }
    return { url, trail };
}

export function copyText(text) {
    try {
        if (typeof navigator !== "undefined" && navigator.clipboard) {
            navigator.clipboard.writeText(text || "");
            return true;
        }
    } catch (_) {
        // ignore
    }
    return false;
}

export function Linkify({ text, withCopy = true, className = "" }) {
    if (text == null) return null;
    const s = String(text);
    if (!URL_RE.test(s)) {
        // Reset stateful regex.
        URL_RE.lastIndex = 0;
        return <span className={className}>{s}</span>;
    }
    URL_RE.lastIndex = 0;
    const parts = [];
    let last = 0;
    let match;
    while ((match = URL_RE.exec(s)) !== null) {
        if (match.index > last) {
            parts.push({ kind: "text", value: s.slice(last, match.index) });
        }
        const { url, trail } = trimUrl(match[0]);
        parts.push({ kind: "url", value: url });
        if (trail) {
            parts.push({ kind: "text", value: trail });
        }
        last = match.index + match[0].length;
    }
    if (last < s.length) {
        parts.push({ kind: "text", value: s.slice(last) });
    }
    return (
        <span className={className} data-testid="linkify-output">
            {parts.map((p, i) =>
                p.kind === "text" ? (
                    <span key={i}>{p.value}</span>
                ) : (
                    <span key={i} className="inline-flex items-center gap-1">
                        <a
                            href={p.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`linkify-anchor-${i}`}
                            className="text-[#FF4400] hover:underline break-all"
                        >
                            {p.value}
                        </a>
                        {withCopy && (
                            <button
                                type="button"
                                onClick={() => copyText(p.value)}
                                title="Copy link"
                                data-testid={`linkify-copy-${i}`}
                                className="text-zinc-500 hover:text-zinc-200 align-middle"
                            >
                                <Copy className="w-3 h-3" />
                            </button>
                        )}
                    </span>
                )
            )}
        </span>
    );
}
